import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { useLoyaltyLevels, LoyaltyLevel } from '@/hooks/useLoyaltyLevels';
import { 
  Cookie, 
  Donut, 
  Croissant, 
  ChefHat, 
  Candy, 
  Award, 
  Medal, 
  Crown, 
  Sparkles,
  Star
} from 'lucide-react';

interface LevelProgressCardProps {
  totalPointsEarned: number;
  currentLevel: number;
}

const getLevelIcon = (iconName: string, size: string = "h-8 w-8") => {
  const iconProps = { className: size };
  
  switch (iconName) {
    case 'cookie':
      return <Cookie {...iconProps} />;
    case 'donut':
      return <Donut {...iconProps} />;
    case 'croissant':
      return <Croissant {...iconProps} />;
    case 'chef-hat':
      return <ChefHat {...iconProps} />;
    case 'candy':
      return <Candy {...iconProps} />;
    case 'award':
      return <Award {...iconProps} />;
    case 'medal':
      return <Medal {...iconProps} />;
    case 'crown':
      return <Crown {...iconProps} />;
    case 'sparkles':
      return <Sparkles {...iconProps} />;
    default:
      return <Star {...iconProps} />;
  }
};

const getRarityColor = (levelNumber: number): string => {
  if (levelNumber >= 9) return 'from-amber-400 via-yellow-300 to-amber-500'; // Gold
  if (levelNumber >= 7) return 'from-purple-500 via-violet-400 to-purple-600'; // Purple
  if (levelNumber >= 5) return 'from-blue-500 via-cyan-400 to-blue-600'; // Blue
  if (levelNumber >= 3) return 'from-green-500 via-emerald-400 to-green-600'; // Green
  return 'from-gray-400 via-slate-300 to-gray-500'; // Gray
};

const LevelProgressCard: React.FC<LevelProgressCardProps> = ({
  totalPointsEarned,
  currentLevel,
}) => {
  const { levels, getCurrentLevel, getNextLevel, getProgressToNextLevel } = useLoyaltyLevels();
  
  const currentLevelData = getCurrentLevel(totalPointsEarned);
  const nextLevelData = getNextLevel(totalPointsEarned);
  const { progress, pointsRemaining } = getProgressToNextLevel(totalPointsEarned);

  if (!currentLevelData) {
    return null;
  }

  const isMaxLevel = !nextLevelData;
  const gradientClass = getRarityColor(currentLevelData.level_number);

  return (
    <Card className="overflow-hidden border-2 border-secondary/30">
      <div className={`bg-gradient-to-r ${gradientClass} p-4 text-white`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
              {getLevelIcon(currentLevelData.icon_name, "h-10 w-10")}
            </div>
            <div>
              <p className="text-sm text-white/80">Nivel {currentLevelData.level_number}</p>
              <h3 className="text-xl font-bold">{currentLevelData.name}</h3>
            </div>
          </div>
          <Badge 
            variant="secondary" 
            className="bg-white/20 text-white border-white/30 text-lg px-3 py-1"
          >
            {totalPointsEarned} pts
          </Badge>
        </div>
      </div>
      
      <CardContent className="p-4">
        {isMaxLevel ? (
          <div className="text-center py-4">
            <Sparkles className="h-8 w-8 text-amber-500 mx-auto mb-2" />
            <p className="text-lg font-semibold text-foreground">
              ¡Has alcanzado el nivel máximo!
            </p>
            <p className="text-sm text-muted-foreground">
              Eres una auténtica Deidad de la Repostería
            </p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                {getLevelIcon(currentLevelData.icon_name, "h-4 w-4 text-muted-foreground")}
                <span className="text-sm text-muted-foreground">Nv. {currentLevelData.level_number}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Nv. {nextLevelData.level_number}</span>
                {getLevelIcon(nextLevelData.icon_name, "h-4 w-4 text-secondary")}
              </div>
            </div>
            
            <Progress 
              value={progress} 
              className="h-3 mb-3" 
            />
            
            <div className="text-center">
              <p className="text-sm text-muted-foreground">
                Te faltan <span className="font-bold text-secondary">{pointsRemaining} puntos</span> para ser
              </p>
              <p className="font-semibold text-foreground flex items-center justify-center gap-2 mt-1">
                {getLevelIcon(nextLevelData.icon_name, "h-5 w-5 text-secondary")}
                {nextLevelData.name}
              </p>
            </div>
          </>
        )}
        
        {/* Level roadmap preview */}
        <div className="mt-4 pt-4 border-t border-border">
          <p className="text-xs text-muted-foreground mb-2 text-center">Niveles</p>
          <div className="flex justify-between items-center overflow-x-auto pb-1">
            {levels.slice(0, 10).map((level) => {
              const isCurrentOrPast = level.level_number <= currentLevel;
              const isCurrent = level.level_number === currentLevel;
              
              return (
                <div 
                  key={level.id}
                  className={`flex flex-col items-center min-w-[32px] ${
                    isCurrent 
                      ? 'text-secondary scale-125' 
                      : isCurrentOrPast 
                        ? 'text-muted-foreground' 
                        : 'text-muted-foreground/40'
                  }`}
                  title={`${level.name} - ${level.min_points} pts`}
                >
                  {getLevelIcon(level.icon_name, "h-4 w-4")}
                  <span className="text-[10px] mt-0.5">{level.level_number}</span>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default LevelProgressCard;
